import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Home from './pages/Home';
import Registro from './pages/Registro';
import Dashboard from './pages/Dashboard';
import Usuarios from './pages/Usuarios';
import Proveedores from './pages/Proveedores';
import Convocatorias from './pages/Convocatorias';
import Evaluacion from './pages/Evaluacion';
import Cotizaciones from './pages/Cotizaciones';
import DetalleConvocatoria from './pages/DetalleConvocatoria';
import MisCotizaciones from './pages/MisCotizaciones';
import PostularCotizacion from './pages/PostularCotizacion';
import ResultadosConvocatoria from './pages/ResultadosConvocatoria';
import VerifyEmail from './pages/VerifyEmail';
import CheckEmail from './pages/CheckEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import PerfilUsuario from './pages/PerfilUsuario';
import MisNotificaciones from './pages/MisNotificaciones';
import OrdenesNotificadasAdmin from './pages/OrdenesNotificadasAdmin';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminLayout from './layouts/AdminLayout';
import PublicLayout from './layouts/PublicLayout';

// Componente para proteger rutas admin (ADMIN y EVALUADOR)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  // Verificar autenticación
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Verificar que el usuario NO sea PROVEEDOR (rol 2)
  // EVALUADOR (rol 3) y ADMIN (rol 1) pueden acceder al admin
  if (user && user.rol === 2) {
    return <Navigate to="/" />;
  }
  
  return <AdminLayout>{children}</AdminLayout>;
};

// Componente para rutas solo de ADMIN (no EVALUADOR)
const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  // Verificar autenticación
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Solo ADMIN (rol 1) puede acceder, no PROVEEDOR (rol 2) ni EVALUADOR (rol 3)
  if (user && (user.rol === 2 || user.rol === 3)) {
    return <Navigate to="/dashboard" />;
  }
  
  return <AdminLayout>{children}</AdminLayout>;
};

// Componente para rutas protegidas (requiere autenticación, cualquier rol)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  // Verificar autenticación
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Si es PROVEEDOR (rol 2), usar PublicLayout
  // Si es ADMIN (rol 1) o EVALUADOR (rol 3), usar AdminLayout
  if (user && user.rol === 2) {
    return <PublicLayout>{children}</PublicLayout>;
  } else {
    return <AdminLayout>{children}</AdminLayout>;
  }
};

// Componente para rutas públicas
const PublicRoute = ({ children }) => {
  return <PublicLayout>{children}</PublicLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Rutas Públicas */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <Home />
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/registro" 
        element={
          <PublicRoute>
            <Registro />
          </PublicRoute>
        } 
      />
      <Route 
        path="/check-email" 
        element={
          <PublicRoute>
            <CheckEmail />
          </PublicRoute>
        } 
      />
      <Route 
        path="/verify-email" 
        element={
          <PublicRoute>
            <VerifyEmail />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        } 
      />
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/perfil" 
        element={
          <ProtectedRoute>
            <PerfilUsuario />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/convocatoria-details/:id" 
        element={
          <PublicRoute>
            <DetalleConvocatoria />
          </PublicRoute>
        } 
      />
      <Route 
        path="/postular-cotizacion/:id" 
        element={
          <PublicRoute>
            <PostularCotizacion />
          </PublicRoute>
        } 
      />
      <Route 
        path="/mis-cotizaciones" 
        element={
          <PublicRoute>
            <MisCotizaciones />
          </PublicRoute>
        } 
      />
      <Route
        path="/mis-ordenes"
        element={
          <PublicRoute>
            <MisNotificaciones />
          </PublicRoute>
        }
      />
      <Route path="/mis-notificaciones" element={<Navigate to="/mis-ordenes" replace />} />
      <Route 
        path="/resultados-convocatoria/:nroConvocatoria" 
        element={
          <PublicRoute>
            <ResultadosConvocatoria />
          </PublicRoute>
        } 
      />
      
      {/* Rutas Admin */}
      <Route
        path="/dashboard"
        element={
          <AdminRoute>
            <Dashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <AdminOnlyRoute>
            <Usuarios />
          </AdminOnlyRoute>
        }
      />
      <Route
        path="/proveedores"
        element={
          <AdminRoute>
            <Proveedores />
          </AdminRoute>
        }
      />
      <Route
        path="/convocatorias"
        element={
          <AdminRoute>
            <Convocatorias />
          </AdminRoute>
        }
      />
      <Route
        path="/evaluacion"
        element={
          <AdminRoute>
            <Evaluacion />
          </AdminRoute>
        }
      />
      <Route
        path="/cotizaciones"
        element={
          <AdminRoute>
            <Cotizaciones />
          </AdminRoute>
        }
      />
      <Route
        path="/ordenes-notificadas"
        element={
          <AdminRoute>
            <OrdenesNotificadasAdmin />
          </AdminRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
